/**
 * Per-line subscription upgrade/downgrade. Posts to /cart/change.js keyed by the
 * line KEY so identity and quantity survive and multi-plan lines of one variant
 * don't collide; selling_plan: null downgrades to a one-time purchase. Controls
 * stay disabled for the round-trip so the select can never show a cadence the
 * cart isn't on.
 */

class CartSubscriptionUpgrade extends HTMLElement {
	static SELECTORS = {
		CONTROLS: '[data-subscription-action]',
		PLAN_SELECT: '[data-subscription-action="change-plan"]',
	};

	constructor() {
		super();
		this.busy = false;
		this.addEventListener('click', this.handleClick.bind(this));
		this.addEventListener('change', this.handleChange.bind(this));
		this.addEventListener('keydown', this.handleKeydown.bind(this));
	}

	// The select sits inside the cart <form>, whose first submit button is cart-summary's name='checkout'.
	// Enter on a focused select is implicit submission, so without this the shopper is thrown to checkout
	// mid-edit. cart-discount.js intercepts Enter on its input for exactly this reason. Nothing is lost:
	// a cadence is committed by 'change', which fires on its own, and while the popup is open the browser
	// keeps keydown to itself so the commit still lands.
	handleKeydown(event) {
		if (event.key !== 'Enter') {
			return;
		}
		if (!event.target.closest(CartSubscriptionUpgrade.SELECTORS.PLAN_SELECT)) {
			return;
		}
		event.preventDefault();
	}

	handleClick(event) {
		const trigger = event.target.closest(CartSubscriptionUpgrade.SELECTORS.CONTROLS);
		// The select shares the action attribute but is driven by 'change', not a click.
		if (!trigger || trigger.tagName === 'SELECT') {
			return;
		}
		event.preventDefault();
		const action = trigger.dataset.subscriptionAction;
		if (action !== 'subscribe') {
			this.changeSellingPlan(null, action);
			return;
		}
		// Guard the plan id: a null plan down the subscribe path leaves the line one-time while announcing a subscription.
		const sellingPlan = trigger.dataset.sellingPlan;
		if (sellingPlan) {
			this.changeSellingPlan(sellingPlan, action);
		}
	}

	// The one-time option carries an empty value: plan ids are numeric, so it can't be mistaken for a cadence.
	handleChange(event) {
		const select = event.target.closest(CartSubscriptionUpgrade.SELECTORS.PLAN_SELECT);
		if (!select) {
			return;
		}
		const sellingPlan = select.value || null;
		this.changeSellingPlan(sellingPlan, sellingPlan ? 'subscribe' : 'onetime');
	}

	setControlsDisabled(isDisabled) {
		this.querySelectorAll(CartSubscriptionUpgrade.SELECTORS.CONTROLS).forEach((control) => {
			control.disabled = isDisabled;
		});
	}

	// != null, not truthiness: on a one-time line the plan the cart holds IS the empty-string sentinel.
	resetSelectToCurrentPlan() {
		const select = this.querySelector(CartSubscriptionUpgrade.SELECTORS.PLAN_SELECT);
		const currentPlan = this.dataset.currentPlan;
		if (select && currentPlan != null) {
			select.value = currentPlan;
		}
	}

	changeSellingPlan(sellingPlan, action) {
		const cartItems = this.closest('cart-items');
		// Re-entry guard: a plan change changes the line key, so a second post would carry a stale one and race two renders.
		if (!cartItems || this.busy) {
			return;
		}
		const key = this.dataset.key;
		const quantity = parseInt(this.dataset.quantity, 10) || 1;
		// Before setControlsDisabled: disabling the focused control unfocuses it, leaving the re-render nothing to restore.
		const focusKey = CartItems.activeFocusKey();
		this.busy = true;
		this.setControlsDisabled(true);
		cartItems.setLoadingState(key);
		window.UFT.Cart.updateLine(key, { quantity, selling_plan: sellingPlan })
			.then(({ ok, state }) => {
				if (!ok) {
					this.handleChangeError(cartItems, key, state);
					return;
				}
				// Move the rollback anchor with the cart: a render failure after a successful mutation would otherwise
				// reset the select to a plan the cart no longer holds.
				this.dataset.currentPlan = sellingPlan || '';
				this.handleChangeSuccess(cartItems, key, action, focusKey);
			})
			.catch((error) => {
				console.error(error);
				this.handleChangeFailure(cartItems, key);
			});
	}

	handleChangeError(cartItems, key, state) {
		this.recoverFromFailure(cartItems, key);
		const message = window.UFT.Cart.errorMessage(state);
		cartItems.displayLineItemError(key, message);
		window.UFT?.CartStatusAnnouncer?.announce?.(message);
	}

	handleChangeFailure(cartItems, key) {
		this.recoverFromFailure(cartItems, key);
		window.UFT?.CartStatusAnnouncer?.announce?.('We could not update your cart. Please try again.', {
			priority: true,
		});
	}

	// Clearing the cart's loading state is part of recovery, or checkout stays disabled.
	recoverFromFailure(cartItems, key) {
		this.busy = false;
		this.setControlsDisabled(false);
		this.resetSelectToCurrentPlan();
		cartItems.clearLoadingState(key);
	}

	handleChangeSuccess(cartItems, key, action, focusKey) {
		const variantId = parseInt(this.dataset.variantId, 10);
		if (variantId) {
			cartItems.dispatchCartUpdated([variantId]);
		}
		const message = action === 'subscribe' ? 'Subscription updated.' : 'Switched to one-time purchase.';
		window.UFT?.CartStatusAnnouncer?.announce?.(message);
		// A successful re-render replaces this element. On failure the plan change has ALREADY landed, so recovering in
		// place alone would leave the select, line price and total all stating a plan the line is no longer on.
		cartItems.rerenderFromFetch(focusKey).catch(() => {
			this.recoverFromFailure(cartItems, key);
			cartItems.recoverFromStaleCart();
		});
	}
}

if (!customElements.get('cart-subscription-upgrade')) {
	customElements.define('cart-subscription-upgrade', CartSubscriptionUpgrade);
}
